import useAccountStore from '@/user/stores/accountStore';
import { useUserStore } from '@/user/stores/userStore';
import ExpandMoreTwoToneIcon from '@mui/icons-material/ExpandMoreTwoTone';
import LockOpenTwoToneIcon from '@mui/icons-material/LockOpenTwoTone';
import {
  Avatar,
  Box,
  Button,
  Divider,
  List,
  ListItemText,
  Popover,
  Typography,
  styled,
  useTheme
} from '@mui/material';
import { useRef, useState } from 'react';
import { useTranslation } from 'next-i18next';
import { UserMenuItems } from '../../../../../common/components/UserMenuItems/UserMenuItems';
import LogoutModal from '../../LogoutModal';
import { UserboxDaysLeftWarning } from '../../UserboxDaysLeftWarning/UserboxDaysLeftWarning';

const UserBoxButton = styled(Button)(
  ({ theme }) => `
        padding: ${theme.spacing(0, 1)};
        height: ${theme.spacing(7)};

        &:hover {
          background-color: transparent;
        }
`
);

const MenuUserBox = styled(Box)(
  ({ theme }) => `
        background: ${theme.colors.alpha.black[5]};
        padding: ${theme.spacing(2)};
`
);

const UserBoxText = styled(Box)(
  ({ theme }) => `  
    text-align: left;
    padding-left: ${theme.spacing(1)};
`
);

const UserBoxLabel = styled(Typography)(
  ({ theme }) => `
        font-weight: ${theme.typography.fontWeightBold};
        color: ${theme.palette.secondary.main};
        display: block;
`
);

function Userbox({ handleCloseMenu }: { handleCloseMenu(): void }) {
  const { t } = useTranslation();
  const theme = useTheme();
  const {
    profile: { email, name, picture }
  } = useUserStore();
  const {
    daysTrialEndNumber,
    status,
    plan: { name: planName }
  } = useAccountStore();
  const ref = useRef<HTMLButtonElement>(null);

  const [isOpen, setOpen] = useState<boolean>(false);
  const [isLogOutOpen, setIsLogoutOpen] = useState<boolean>(false);

  const handleOpen = (): void => {
    setOpen(true);
  };

  const handleClose = (): void => {
    setOpen(false);
    handleCloseMenu();
  };

  const handleOnLogoutOpen = (): void => {
    setOpen(false);
    setIsLogoutOpen(true);
  };

  return (
    <>
      <UserBoxButton color="primary" ref={ref} onClick={handleOpen}>
        <Avatar variant="circular" alt={name} src={picture} />
        <UserBoxText>
          <UserBoxLabel variant="body1">{name || email}</UserBoxLabel>
        </UserBoxText>
        <Box component="span">
          <ExpandMoreTwoToneIcon
            sx={{
              ml: 1
            }}
          />
        </Box>
      </UserBoxButton>
      <Popover
        disableScrollLock
        anchorEl={ref.current}
        onClose={handleClose}
        open={isOpen}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'right'
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right'
        }}
      >
        <MenuUserBox
          component="span"
          display="flex"
          alignItems="center"
          width={312}
        >
          <Avatar variant="circular" alt={name} src={picture} />
          <UserBoxText>
            <UserBoxLabel variant="body1">{name || email}</UserBoxLabel>
            {status === 'ACTIVE' && <>{planName}</>}
            {status === 'TRIALING' && (
              <UserboxDaysLeftWarning
                daysLeft={daysTrialEndNumber}
                planName="Trial"
              />
            )}
          </UserBoxText>
        </MenuUserBox>
        <Divider
          sx={{
            mb: 0
          }}
        />
        <List
          sx={{
            p: 1
          }}
          component="nav"
        >
          <Box color={theme.sidebar.textColor}>
            <ListItemText primary={t('Account')} sx={{ py: 1, pl: 2 }} />
            <UserMenuItems handleCloseMenu={handleClose} />
          </Box>
        </List>
        <Divider />
        <Box m={1}>
          <Button color="primary" fullWidth onClick={handleOnLogoutOpen}>
            <LockOpenTwoToneIcon
              sx={{
                mr: 1
              }}
            />
            {t('Sign out')}
          </Button>
        </Box>
      </Popover>
      <LogoutModal
        handleClose={handleClose}
        isLogOutOpen={isLogOutOpen}
        setIsLogoutOpen={setIsLogoutOpen}
      />
    </>
  );
}

export default Userbox;
